import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractComponent } from '@shared/common/abstract.component';
import type {
    MinimumContractValue } from '@shared/modules/contracts/common/minimum-contract-value/minimum-contract-value';
import {
    MinimumContractValueSet,
    MinimumContractValueType,
} from '@shared/modules/contracts/common/minimum-contract-value/minimum-contract-value';
import { clone } from '@shared/utils';
import { equals } from '@shared/utils/helpers';
import get from 'lodash-es/get';

@Component({
    selector: 'app-minimum-contract-value',
    styleUrls: ['./minimum-contract-value.component.scss'],
    template: `
        <div class="minimum-contract-value-container">
            <cll-card class="app-form-card">
                <cll-card-header>
                    <cll-card-title>Minimum Contract Value</cll-card-title>

                    <cll-card-header-actions style="display: flex; flex-direction: row; align-items: center;">
                        <app-minimum-contract-value-period-select
                            *ngIf="enabled"
                            [(ngModel)]="_minimumContractValue.type"
                            (ngModelChange)="_onModelChanged()"
                            appearance="standard"
                            style="padding-right: 20px;" />
                        <div>
                            <button cll-outline-button
                                    colour="variant"
                                    data-testid="button:disabled"
                                    (click)="_toggleEnabled()"
                                    *ngIf="!enabled"
                                    cllTooltip="Click to enable">
                                <mat-icon>clear</mat-icon>
                                Disabled
                            </button>

                            <button cll-outline-button
                                    colour="variant"
                                    (click)="_toggleEnabled()"
                                    *ngIf="enabled"
                                    cllTooltip="Click to disable">
                                <mat-icon>check</mat-icon>
                                Enabled
                            </button>
                        </div>
                    </cll-card-header-actions>
                </cll-card-header>

                <ng-container *ngIf="enabled">
                    <p>
                        If the minimum amount for the given period has not been met, the remainder will be calculated and added to the
                        invoice.
                        The remainder will be added on the last day of every period that the invoice run covers.
                    </p>

                    <div *ngIf="_minimumContractValue.type === 'weekly'" class="form-row">
                        <app-input-currency
                            label="Amount"
                            data-testid="input:minimum-contract-value-amount"
                            class="flex-50"
                            appearance="standard"
                            [(ngModel)]="_minimumContractValue.weekly_amount"
                            (ngModelChange)="_onModelChanged()" />

                        <app-input-number
                            label="Hours"
                            data-testid="input:minimum-contract-value-hours"
                            class="flex-50"
                            appearance="standard"
                            [min]="0"
                            [max]="168"
                            [step]=".25"
                            [(ngModel)]="_minimumContractValue.weekly_hours"
                            (ngModelChange)="_onModelChanged()" />
                    </div>

                    <div *ngIf="_minimumContractValue.type === 'daily'" class="form-row">
                        <app-input-currency
                            label="Amount"
                            class="flex-50"
                            appearance="standard"
                            [(ngModel)]="_minimumContractValue.daily_amount"
                            (ngModelChange)="_onModelChanged()" />

                        <app-input-number
                            label="Hours"
                            class="flex-50"
                            appearance="standard"
                            [min]="0"
                            [max]="24"
                            [step]=".25"
                            [(ngModel)]="_minimumContractValue.daily_hours"
                            (ngModelChange)="_onModelChanged()" />
                    </div>

                    <hr class="app-separator">

                    <app-input-text
                        label="Label (optional)"
                        data-testid="input:minimum-contract-value-label"
                        hint="How it will be displayed on the invoice"
                        [(ngModel)]="_minimumContractValue.label"
                        (ngModelChange)="_onModelChanged()" />

                    <hr class="app-separator">

                    <app-contract-rules-list [(ngModel)]="_minimumContractValue.rules"
                                             (ngModelChange)="_onModelChanged()" />
                </ng-container>
            </cll-card>
        </div>
    `,
})
export class MinimumContractValueComponent extends AbstractComponent {
    enabled: boolean;

    _minimumContractValue: MinimumContractValue;
    _minimumContractValueOriginal: MinimumContractValue;

    @Input()
    set minimumContractValue(value: MinimumContractValueSet) {
        if (get(value, 'data')) {
            this._minimumContractValue = value.data;
            this._minimumContractValueOriginal = value.data;
            this.enabled = true;
        } else {
            this._minimumContractValue = this._getEmptyMinimumContractValue();
            this._minimumContractValueOriginal = null;
            this.enabled = false;
        }
    }

    @Output() minimumContractValueChange = new EventEmitter<MinimumContractValueSet>();

    _onModelChanged() {
        const previousMinimum = clone(this._minimumContractValueOriginal);
        const updatedMinimum = clone(this._minimumContractValue);

        const hasChanged = !equals(previousMinimum, updatedMinimum);

        if (hasChanged) {
            this.minimumContractValueChange.emit({
                data: updatedMinimum
            });
        }
    }

    _toggleEnabled() {
        this.enabled = !this.enabled;

        if (!this.enabled) {
            this._minimumContractValue = null;
        }

        this._onModelChanged();
    }

    _getEmptyMinimumContractValue() {
        return {
            owner_type: '',
            owner_id: '',
            label: '',
            type: MinimumContractValueType.weekly,
            weekly_amount: 0,
            weekly_hours: 0,
            daily_amount: 0,
            daily_hours: 0,
            rules: null,
        };
    }
}
